import { forwardRef, type ReactNode } from 'react';
import { Select as S } from 'radix-ui';
import { CaretDownIcon, CheckIcon } from '@phosphor-icons/react';
import { cn } from '@/lib/cn';
import { floatingPanel, menuItem } from './overlay-styles';

export interface SelectOption {
  value: string;
  label: ReactNode;
  disabled?: boolean;
  /** Small muted text on the right (e.g. price, count). */
  meta?: ReactNode;
}

export interface SelectProps {
  value?: string;
  defaultValue?: string;
  onValueChange?: (v: string) => void;
  options: SelectOption[];
  placeholder?: string;
  disabled?: boolean;
  invalid?: boolean;
  size?: 'sm' | 'md';
  className?: string;
  id?: string;
  name?: string;
  'aria-label'?: string;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean;
}

export const Select = forwardRef<HTMLButtonElement, SelectProps>(function Select(
  {
    options,
    placeholder,
    invalid,
    size = 'md',
    className,
    id,
    value,
    defaultValue,
    onValueChange,
    disabled,
    name,
    ...aria
  },
  ref,
) {
  return (
    <S.Root
      value={value}
      defaultValue={defaultValue}
      onValueChange={onValueChange}
      disabled={disabled}
      name={name}
    >
      <S.Trigger
        ref={ref}
        id={id}
        aria-invalid={invalid || aria['aria-invalid'] || undefined}
        aria-label={aria['aria-label']}
        aria-describedby={aria['aria-describedby']}
        className={cn(
          'group inline-flex w-full items-center justify-between gap-2 rounded-sm border border-control-border bg-surface text-left text-ink',
          'shadow-[0_1px_0_var(--gh-hairline)] transition-[border-color,box-shadow] hover:border-ink-subtle/60',
          'focus:outline-none focus-visible:border-primary focus-visible:shadow-[0_0_0_3px_var(--gh-primary-soft)]',
          'data-[placeholder]:text-ink-subtle disabled:cursor-not-allowed disabled:bg-sunken disabled:opacity-70',
          'aria-[invalid=true]:border-negative',
          size === 'sm' ? 'h-8 px-2.5 text-sm' : 'h-9.5 px-3 text-[0.875rem]',
          className,
        )}
      >
        <span className="min-w-0 truncate">
          <S.Value placeholder={placeholder} />
        </span>
        <S.Icon className="shrink-0 text-ink-subtle transition-transform duration-150 group-data-[state=open]:rotate-180">
          <CaretDownIcon className="size-4" />
        </S.Icon>
      </S.Trigger>
      <S.Portal>
        <S.Content
          position="popper"
          sideOffset={6}
          className={cn(
            floatingPanel,
            'max-h-[min(22rem,var(--radix-select-content-available-height))] min-w-[var(--radix-select-trigger-width)]',
          )}
        >
          <S.Viewport className="p-1">
            {options.map((o) => (
              <S.Item
                key={o.value}
                value={o.value}
                disabled={o.disabled}
                className={cn(menuItem, 'pr-8')}
              >
                <S.ItemText>{o.label}</S.ItemText>
                {o.meta && <span className="ml-auto text-xs text-ink-subtle">{o.meta}</span>}
                <S.ItemIndicator className="absolute right-2.5 inline-flex">
                  <CheckIcon weight="bold" className="!text-primary-ink" />
                </S.ItemIndicator>
              </S.Item>
            ))}
          </S.Viewport>
        </S.Content>
      </S.Portal>
    </S.Root>
  );
});
